import {
  Box,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CustomButton from "components/common/CustomButton";
import PaginationControls from "components/fileStorage/PaginationControls";
import IntegrationDetailsTable from "components/integrations-list/IntegrationDetailsTable";

import {
  GetIntegrationHistoryParams,
  IntegrationDetails,
  useGetIntegrationsHistoryQuery,
} from "features/integrations/integrations";
import { integrationsApi } from "features/integrations/integrations";
import { useUpdateIntegrationNameMutation } from "features/integrations/integrations";
import { useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "features/store";
import { useNavigate, useParams } from "react-router-dom";
import { getTimezoneName } from "utils/dateUtils";
import Layout from "../components/Layout";

export default function IntegrationDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sortBy, setSortBy] =
    useState<GetIntegrationHistoryParams["sort_by"]>();
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [useIntegrationTimezone, setUseIntegrationTimezone] = useState(true);

  const queryArgs = useMemo(
    () => ({
      integrationId: parseInt(id!, 10),
      page,
      page_size: pageSize,
      ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
    }),
    [id, page, pageSize, sortBy, sortOrder],
  );

  const { data, isFetching, refetch } = useGetIntegrationsHistoryQuery(
    queryArgs,
    {
      skip: !id,
    },
  );

  const [updateIntegrationName, { isLoading: isUpdating }] =
    useUpdateIntegrationNameMutation();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [newIntegrationName, setNewIntegrationName] = useState("");
  const [updateError, setUpdateError] = useState<string | null>(null);

  const history = data?.results ?? [];
  const integration = data?.integration_details as
    | IntegrationDetails
    | undefined;

  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  const handleSortChange = (field: GetIntegrationHistoryParams["sort_by"]) => {
    if (sortBy === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else if (sortOrder === "desc") {
        setSortBy(undefined);
        setSortOrder("asc");
      }
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const onBack = () => navigate("/integrations");

  const onOpenEdit = () => {
    if (integration?.integration) {
      setNewIntegrationName(integration.integration);
    }
    setUpdateError(null);
    setIsEditOpen(true);
  };

  const onCloseEdit = () => {
    setIsEditOpen(false);
    setUpdateError(null);
  };

  const onSaveEdit = async () => {
    if (!id) return;
    const trimmed = (newIntegrationName || "").trim();
    if (!trimmed) {
      setUpdateError("Integration name cannot be empty");
      return;
    }
    try {
      await updateIntegrationName({
        connection_match_id: parseInt(id, 10),
        new_integration_name: trimmed,
      }).unwrap();
      setIsEditOpen(false);
      setUpdateError(null);
      // Optimistically update RTK Query cache to avoid refetch and full rerender
      dispatch(
        integrationsApi.util.updateQueryData(
          "getIntegrationsHistory",
          { ...queryArgs },
          (draft) => {
            if (draft && draft.integration_details) {
              draft.integration_details.integration = trimmed;
            }
          },
        ),
      );
    } catch (e: any) {
      const msg =
        e?.data?.error || e?.data?.message || e?.message || "Update failed";
      setUpdateError(String(msg));
    }
  };

  return (
    <Layout title="Integration Details" content="IntegrationDetails">
      <Container
        component="main"
        maxWidth={false}
        sx={{
          maxWidth: "1200px",
          width: "100%",
          py: 3,
          px: { xs: 1.5, sm: 3 },
          height: "calc(100vh - 200px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isFetching ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexGrow={1}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              gap={1}
              mb={2}
            >
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "Century Gothic, sans-serif",
                  fontWeight: "bold",
                  color: "rgb(31,93,107)",
                }}
              >
                Integration Details
              </Typography>
              <CustomButton fullWidth={false} onClick={onBack}>
                Back
              </CustomButton>
            </Box>

            {integration && (
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: 2 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1}
                >
                  <Typography variant="h6">
                    {integration.integration}
                  </Typography>
                  {user?.is_calibrate_admin && (
                    <Tooltip title="Edit name">
                      <span>
                        <IconButton
                          size="small"
                          onClick={onOpenEdit}
                          disabled={isUpdating}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Type:
                      </Typography>
                      <Typography variant="body1">
                        {integration.type}
                      </Typography>
                    </Box>
                    <Box mt={2}>
                      <Typography variant="body2" color="text.secondary">
                        Timezone:
                      </Typography>
                      <Typography variant="body1">
                        {getTimezoneName(integration.timezone)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Frequency:
                      </Typography>
                      {integration.frequency &&
                      integration.frequency.length > 0 ? (
                        <Box>
                          {integration.frequency.map((f, i) => (
                            <Typography key={i} variant="body1">
                              {f}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body1">—</Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            )}
            <Dialog
              open={isEditOpen}
              onClose={onCloseEdit}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>Edit Integration Name</DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  fullWidth
                  margin="dense"
                  label="Integration Name"
                  value={newIntegrationName}
                  onChange={(e) => setNewIntegrationName(e.target.value)}
                  disabled={isUpdating}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onSaveEdit();
                    }
                  }}
                />
                {updateError && (
                  <Typography variant="body2" color="error" mt={1}>
                    {updateError}
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                <CustomButton
                  fullWidth={false}
                  variant="outlined"
                  onClick={onCloseEdit}
                  disabled={isUpdating}
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  fullWidth={false}
                  onClick={onSaveEdit}
                  disabled={isUpdating}
                >
                  Save
                </CustomButton>
              </DialogActions>
            </Dialog>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,
                height: 40,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 600, color: "primary.main" }}
              >
                Time:
              </Typography>
              <ToggleButtonGroup
                value={useIntegrationTimezone ? "integration" : "local"}
                exclusive
                onChange={(_, value: string | null) => {
                  if (value !== null) {
                    setUseIntegrationTimezone(value === "integration");
                  }
                }}
                size="small"
                sx={{ height: "100%", "& .MuiToggleButton-root": { py: 0 } }}
              >
                <ToggleButton value="integration" aria-label="Integration time">
                  Integration
                </ToggleButton>
                <ToggleButton value="local" aria-label="Local time">
                  Local
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <IntegrationDetailsTable
              history={history}
              isFetching={isFetching}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
              timezone={
                useIntegrationTimezone ? integration?.timezone : undefined
              }
              pagination={
                history.length > 0 && totalItems > 0 ? (
                  <Box
                    sx={{
                      position: "relative",
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <PaginationControls
                      currentLimit={pageSize}
                      onLimitChange={(limit) => {
                        setPageSize(limit);
                        setPage(1);
                      }}
                      totalItems={totalItems}
                      currentPage={page}
                      totalPages={totalPages}
                      onPageChange={setPage}
                      showLimitSelector={true}
                      showPageInfo={true}
                      disabled={isFetching}
                      size="small"
                      pageTokens={[]}
                      hasNextPage={page < totalPages}
                      borderStyle={{ borderTop: "none", borderColor: "none" }}
                      buttonStyle={{
                        border: "1px solid #e5e7eb",
                        borderColor: "",
                      }}
                    />
                  </Box>
                ) : null
              }
            />
          </>
        )}
      </Container>
    </Layout>
  );
}
